/**
 * Changing who sees a folder. One with anything in it is asked about first: whether what's inside takes the change
 * too ("Apply to the 3 folders and 41 items inside"), checked by default when the change narrows who sees it and not
 * when it widens it, and said plainly when narrowing leaves what's inside seen more widely. The answer is always sent
 * (`apply_to_contents`); the server's other refusals (private items going outside, a name the space has) are asked
 * about in turn.
 */
import type { ComponentChildren } from "preact";
import { useRef, useState } from "preact/hooks";
import { ApiError, api } from "../../api/client.ts";
import type { FolderCounts, FolderOut, FolderPatch, FolderRef, Job, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { applied, narrows, numbered, type Sharing, sameSharing } from "../../lib/folders.ts";
import { followJob } from "../../lib/jobs.ts";
import { spaceApi } from "../../lib/paths.ts";
import { asApiError, bump, useAction } from "../../lib/resource.ts";
import { me } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { ConfirmDialog, Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { ApplyForm, type Asking as AskingOf } from "./applyForm.tsx";

/** A folder as its sharing is changed: who sees it, its version, its name, and what it holds when known. */
export type SharedFolder = Sharing & { id: string; ver: string; name: string; counts?: FolderCounts | undefined };

/** A change of who sees a folder (its PATCH, without what's asked here). */
export type SharingChange = Pick<FolderPatch, "visibility" | "private_to" | "guests" | "public" | "name">;

const APPLY_REQUIRED = "apply_to_contents_required";
const PRIVATE_CONTENTS = "private_contents";
const NAME_TAKEN = "name_taken";
/** How many free names are tried before giving up ("Acme (2)" to "Acme (20)"). */
const TRIES = 20;
const FIRST_FREE = 2;
/** What each change of who has access says. */
const MADE = {
  private: "visibility.madePrivate",
  org: "visibility.madeOrg",
  public: "visibility.madePublic",
} as const;

/** The question about what's inside, of a change of sharing. */
type Asking = AskingOf<SharingChange>;

const countsIn = (e: ApiError, key: "counts" | "wider"): FolderCounts | null =>
  (e.info[key] as FolderCounts | null | undefined) ?? null;

/**
 * Changing folder `folder`'s sharing: `change` makes one (asking what it needs to), `dialogs` are where it asks
 * (`nested` inside a popover, which stays open behind). `onDone` gets the folder as it is now (after a job: nothing).
 */
export function useFolderSharing(
  space: SpaceOut,
  folder: SharedFolder,
  onDone: (f: FolderOut | null) => void,
  nested = false,
): { change: (body: SharingChange) => Promise<void>; busy: boolean; dialogs: ComponentChildren } {
  const [asking, setAsking] = useState<Asking | null>(null);
  const [outside, setOutside] = useState<{ body: FolderPatch; items: { id: string; title: string }[] } | null>(null);
  const [clash, setClash] = useState<{ body: FolderPatch; inside: FolderRef[]; message: string } | null>(null);
  // The question on screen (a probe's answer for an older one is dropped).
  const shown = useRef<Asking | null>(null);
  shown.current = asking;
  const path = spaceApi(space, `/folders/${folder.id}`);

  /** Makes the change (a job past a thousand things inside, followed until it's done). */
  const patch = async (body: FolderPatch): Promise<void> => {
    const r = await api.patch<FolderOut | Job>(path, { ...body, ver: folder.ver });
    if ("action" in r) {
      await followJob(space, r);
      onDone(null);
    } else {
      onDone(r);
    }
    bump();
    if (body.visibility) {
      toast(t(MADE[body.visibility]));
    }
  };

  /** [`patch`], asking what the server's refusals need. */
  const send = async (body: FolderPatch): Promise<void> => {
    try {
      await patch(body);
    } catch (e) {
      const err = asApiError(e);
      if (err.code === PRIVATE_CONTENTS) {
        setOutside({ body, items: (err.info["items"] as { id: string; title: string }[] | undefined) ?? [] });
        return;
      }
      if (err.code === NAME_TAKEN) {
        setClash({ body, inside: (err.info["folders"] as FolderRef[] | undefined) ?? [], message: err.message });
        return;
      }
      if (err.code === APPLY_REQUIRED) {
        // What it holds now (the list's count was of what it shows, or old).
        const counts = countsIn(err, "counts") ?? { folders: 0, items: 0, hidden: false };
        const after = applied(folder, body, me.value?.id);
        setAsking({ body, counts, narrowing: narrows(folder, after), wider: countsIn(err, "wider") });
        return;
      }
      throw err;
    }
  };

  /** Asks the server what narrowing would leave seen more widely (it refuses the change, saying so). */
  const probe = async (q: Asking): Promise<void> => {
    try {
      await api.patch(path, { ...q.body, ver: folder.ver });
    } catch (e) {
      if (e instanceof ApiError && e.code === APPLY_REQUIRED && shown.current === q) {
        setAsking({ ...q, wider: countsIn(e, "wider") });
      }
    }
  };

  const change = async (body: SharingChange): Promise<void> => {
    const after = applied(folder, body, me.value?.id);
    const counts = folder.counts;
    // Nobody gained or lost (what its public page shows, say): nothing to ask.
    if (sameSharing(folder, after) || !counts || counts.folders + counts.items === 0) {
      await send(body);
      return;
    }
    const q: Asking = { body, counts, narrowing: narrows(folder, after), wider: null };
    setAsking(q);
    if (q.narrowing) {
      shown.current = q;
      await probe(q);
    }
  };

  const [run, busy] = useAction(send);
  /** Renamed to the first free name ("Acme (2)"), then shared as asked. */
  const [rename, renaming] = useAction(async (body: FolderPatch) => {
    for (let n = FIRST_FREE; n < FIRST_FREE + TRIES; n += 1) {
      try {
        // biome-ignore lint/performance/noAwaitInLoops: each name is tried after the one before was taken.
        await patch({ ...body, name: numbered(folder.name, n) });
        setClash(null);
        return;
      } catch (e) {
        if (!(e instanceof ApiError && e.code === NAME_TAKEN)) {
          throw e;
        }
      }
    }
  });
  const dialogs = (
    <>
      <Dialog
        nested={nested}
        open={asking !== null}
        onClose={() => setAsking(null)}
        title={t("folders.share.title", { name: folder.name })}
      >
        {asking ? (
          <ApplyForm
            key={JSON.stringify(asking.body)}
            asking={asking}
            onCancel={() => setAsking(null)}
            onApply={(apply) => {
              setAsking(null);
              run({ ...asking.body, apply_to_contents: apply });
            }}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        nested={nested}
        open={outside !== null}
        onClose={() => setOutside(null)}
        title={t("folders.share.privateTitle")}
        message={
          <>
            <p>{t("folders.share.private", { count: outside?.items.length ?? 0 })}</p>
            <ul class="mt-2 list-disc pl-5">
              {(outside?.items ?? []).map((a) => (
                <li key={a.id}>{a.title || t("common.untitled")}</li>
              ))}
            </ul>
          </>
        }
        confirmLabel={t("visibility.shareThemToo")}
        onConfirm={() => (outside ? run({ ...outside.body, share_private: true }) : undefined)}
      />
      <Dialog nested={nested} open={clash !== null} onClose={() => setClash(null)} title={t("folders.nameTakenTitle")}>
        {clash ? (
          <>
            <p class="text-sm text-zinc-700">{clash.message}</p>
            {clash.inside.length > 0 ? (
              <ul class="list-disc pl-5 text-sm text-zinc-700">
                {clash.inside.map((f) => (
                  <li key={f.id}>{f.path}</li>
                ))}
              </ul>
            ) : null}
            <DialogActions>
              <Button answer="cancel" onClick={() => setClash(null)}>
                {t("common.cancel")}
              </Button>
              {/* Its own name clashes: a free one. Those inside are renamed one by one, where they are. */}
              {clash.inside.length === 0 ? (
                <Button answer="confirm" variant="primary" busy={renaming} onClick={() => rename(clash.body)}>
                  {t("folders.share.renameAndShare", { name: numbered(folder.name, FIRST_FREE) })}
                </Button>
              ) : null}
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </>
  );
  return { change, busy: busy || renaming, dialogs };
}
